import * as React from 'react'
import styled from 'styled-components'
import { rem, transparentize } from 'polished'
import Group from 'components/utils/Group'
import ScrollToBottomButton from './ScrollToBottomButton'

const ChatColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
`

const MessagesWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  padding: ${rem(20)} ${rem(20)} ${rem(24)};

  @media (max-width: 768px) {
    padding: ${rem(20)};
  }
`

const TopSlotWrapper = styled.div`
  position: relative;
  z-index: 3;
  flex-shrink: 0;
`

const InputAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`

const ChatOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: ${props => transparentize(0.97, props.theme.palette.neutral.surface.darkBackground)};
  display: block;
  pointer-events: all;
`

const ChatContainer = styled(Group)`
  height: 100%;
  min-height: ${rem(520)};
  border: none;
  border-radius: 0;
  position: relative;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const BottomSpacer = styled.div`
  min-height: var(--spacer-height, 0);
  flex-shrink: 0;
`

interface Props {
  /** Message content rendered inside the scroll container */
  children: React.ReactNode
  messagesWrapperRef: React.MutableRefObject<HTMLDivElement | null>
  onScroll: () => void
  isOverlay: boolean
  showHistoryOverlay: boolean
  onHistoryWheel?: (e: React.WheelEvent<HTMLDivElement>) => void
  onHistoryTouchStart?: (e: React.TouchEvent<HTMLDivElement>) => void
  onHistoryTouchMove?: (e: React.TouchEvent<HTMLDivElement>) => void
  onHistoryTouchEnd?: () => void
  bottomPaddingPx?: number
  topSlot?: React.ReactNode
  inputSlot?: React.ReactNode
  onPullToCollapseTouchStart?: (e: React.TouchEvent<HTMLDivElement>) => void
  onPullToCollapseTouchMove?: (e: React.TouchEvent<HTMLDivElement>) => void
  onPullToCollapseTouchEnd?: () => void
  /** Whether the user is currently near the bottom of the scroll container */
  isAtBottom?: boolean
  /** Whether there is content below the current viewport */
  hasContentOutOfView?: boolean
  /** Callback when the scroll-to-bottom button is clicked */
  onScrollToBottomButtonClick?: () => void
  /** Whether user is viewing history (disables scroll button) */
  isViewingHistory?: boolean
  dynamicSpacerHeight?: number
}

export default function ChatPanel({
  children,
  messagesWrapperRef,
  onScroll,
  isOverlay,
  showHistoryOverlay,
  onHistoryWheel,
  onHistoryTouchStart,
  onHistoryTouchMove,
  onHistoryTouchEnd,
  bottomPaddingPx,
  topSlot,
  inputSlot,
  onPullToCollapseTouchStart,
  onPullToCollapseTouchMove,
  onPullToCollapseTouchEnd,
  isAtBottom = true,
  hasContentOutOfView = false,
  onScrollToBottomButtonClick,
  isViewingHistory = false,
  dynamicSpacerHeight = 0,
}: Props) {
  // Show the scroll-to-bottom button when:
  // - There is content out of view
  // - User is not at the bottom
  // - Not viewing history
  // - Not in overlay mode (mobile collapsed state)
  const showScrollButton = hasContentOutOfView && !isAtBottom && !isViewingHistory && !isOverlay

  return (
    <ChatColumn>
      <ChatContainer direction="vertical" gap={0} fullWidth>
        {topSlot && <TopSlotWrapper>{topSlot}</TopSlotWrapper>}
        <MessagesWrapper
          ref={messagesWrapperRef}
          onScroll={onScroll}
          onTouchStart={onPullToCollapseTouchStart}
          onTouchMove={onPullToCollapseTouchMove}
          onTouchEnd={onPullToCollapseTouchEnd}
          style={bottomPaddingPx ? { paddingBottom: `${bottomPaddingPx}px` } : undefined}
        >
          {children}
          <BottomSpacer data-spacer style={{ '--spacer-height': `${dynamicSpacerHeight}px` } as React.CSSProperties} />
        </MessagesWrapper>
        {!isOverlay && (
          <InputAreaWrapper>
            <ScrollToBottomButton visible={showScrollButton} onClick={onScrollToBottomButtonClick} />
            {inputSlot}
          </InputAreaWrapper>
        )}
        {showHistoryOverlay && (
          <ChatOverlay
            role="presentation"
            aria-hidden="true"
            onWheel={onHistoryWheel}
            onTouchStart={onHistoryTouchStart}
            onTouchMove={onHistoryTouchMove}
            onTouchEnd={onHistoryTouchEnd}
            onTouchCancel={onHistoryTouchEnd}
          />
        )}
      </ChatContainer>
    </ChatColumn>
  )
}
